import type { DifficultyRating } from './types.js';
import { puzzleFromShape } from './generator/from-shape.js';
import { solve, isUnique } from './solver/index.js';
import { rateDifficulty } from './difficulty.js';

/** A rectangular region within a grid. */
export interface GridRegion {
  row: number;
  col: number;
  width: number;
  height: number;
}

/** Per-region analysis produced by autoSlice. */
export interface RegionAnalysis {
  empty: boolean;
  unique: boolean | null;
  /** @deprecated Use sweepCount instead */
  difficulty: DifficultyRating | null;
  /** Number of alternating sweeps needed to solve (null if empty) */
  sweepCount: number | null;
  /** Whether the puzzle requires backtracking (null if empty) */
  requiresBacktracking: boolean | null;
}

/** Options for BSP-based auto-slicing. */
export interface AutoSliceOptions {
  /** Minimum region dimension (default: 3) */
  minSize?: number;
  /** Maximum region dimension (default: 8) */
  maxSize?: number;
  /** Reject partitions that contain empty regions (default: false) */
  noEmpty?: boolean;
  /** Require all non-empty regions to have unique solutions (default: false) */
  requireUnique?: boolean;
  /** @deprecated Use minSweepCount/maxSweepCount instead */
  minDifficulty?: DifficultyRating;
  /** @deprecated Use minSweepCount/maxSweepCount instead */
  maxDifficulty?: DifficultyRating;
  /** Minimum sweep count for every non-empty region */
  minSweepCount?: number;
  /** Maximum sweep count for every non-empty region */
  maxSweepCount?: number;
  /** Maximum number of BSP attempts (default: 10000) */
  maxAttempts?: number;
  /** When true, all regions will have the same width and height (grid-based) */
  uniformSize?: boolean;
}

/** Result of auto-slicing a grid. */
export interface AutoSliceResult {
  regions: GridRegion[];
  /** Per-region analysis (same order as regions) */
  analysis: RegionAnalysis[];
  /** Whether ALL constraints were satisfied */
  constraintsSatisfied: boolean;
  /** How many BSP attempts were evaluated */
  attempts: number;
}

const DIFFICULTY_RANK: Record<DifficultyRating, number> = {
  trivial: 0,
  easy: 1,
  medium: 2,
  hard: 3,
  expert: 4,
};

/** Extract a rectangular sub-grid from a larger grid. */
export function sliceGrid(
  grid: boolean[][],
  region: GridRegion,
): boolean[][] {
  const result: boolean[][] = [];
  for (let r = 0; r < region.height; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < region.width; c++) {
      row.push(grid[region.row + r]?.[region.col + c] ?? false);
    }
    result.push(row);
  }
  return result;
}

/** Check if a region of the grid is entirely empty (no filled cells). */
export function isRegionEmpty(grid: boolean[][], region: GridRegion): boolean {
  for (let r = 0; r < region.height; r++) {
    const row = grid[region.row + r];
    if (!row) continue;
    for (let c = 0; c < region.width; c++) {
      if (row[region.col + c]) return false;
    }
  }
  return true;
}

/**
 * BSP (Binary Space Partitioning) random grid partitioning.
 *
 * Recursively splits a rectangle into smaller rectangles. Each dimension
 * stays within [minSize, maxSize]. Splits are randomized for variety —
 * even regions within the acceptable range may be split further.
 */
function bspPartition(
  row: number,
  col: number,
  width: number,
  height: number,
  minSize: number,
  maxSize: number,
): GridRegion[] {
  if (width <= maxSize && height <= maxSize) {
    const canSplitH = height >= minSize * 2;
    const canSplitV = width >= minSize * 2;

    if (canSplitV && Math.random() > 0.55) {
      const lo = minSize;
      const hi = width - minSize;
      const splitAt = lo + Math.floor(Math.random() * (hi - lo + 1));
      return [
        ...bspPartition(row, col, splitAt, height, minSize, maxSize),
        ...bspPartition(row, col + splitAt, width - splitAt, height, minSize, maxSize),
      ];
    }
    if (canSplitH && Math.random() > 0.55) {
      const lo = minSize;
      const hi = height - minSize;
      const splitAt = lo + Math.floor(Math.random() * (hi - lo + 1));
      return [
        ...bspPartition(row, col, width, splitAt, minSize, maxSize),
        ...bspPartition(row + splitAt, col, width, height - splitAt, minSize, maxSize),
      ];
    }
    return [{ row, col, width, height }];
  }

  const canSplitH = height >= minSize * 2;
  const canSplitV = width >= minSize * 2;

  if (!canSplitH && !canSplitV) {
    return [{ row, col, width, height }];
  }

  let splitH: boolean;
  if (canSplitH && canSplitV) {
    splitH = height > width ? true : width > height ? false : Math.random() > 0.5;
  } else {
    splitH = canSplitH;
  }

  if (splitH) {
    const lo = Math.max(minSize, height - maxSize);
    const hi = Math.min(height - minSize, maxSize);
    const splitAt = lo + Math.floor(Math.random() * Math.max(1, hi - lo + 1));
    return [
      ...bspPartition(row, col, width, splitAt, minSize, maxSize),
      ...bspPartition(row + splitAt, col, width, height - splitAt, minSize, maxSize),
    ];
  } else {
    const lo = Math.max(minSize, width - maxSize);
    const hi = Math.min(width - minSize, maxSize);
    const splitAt = lo + Math.floor(Math.random() * Math.max(1, hi - lo + 1));
    return [
      ...bspPartition(row, col, splitAt, height, minSize, maxSize),
      ...bspPartition(row, col + splitAt, width - splitAt, height, minSize, maxSize),
    ];
  }
}

/**
 * Generate random square-region partitions using horizontal bands.
 *
 * Each band has a height chosen from valid square sizes (sizes that
 * divide gridWidth evenly). Within each band, the grid is split into
 * squares of that band's height. Different bands can use different sizes,
 * producing layouts like "2x 10x10 on top, 8x 5x5 on bottom".
 */
function squarePartitions(
  gridWidth: number,
  gridHeight: number,
  minSize: number,
  maxSize: number,
  count: number,
): GridRegion[][] {
  // Valid square sizes: must divide gridWidth evenly
  const validSizes: number[] = [];
  for (let s = minSize; s <= maxSize; s++) {
    if (gridWidth % s === 0) validSizes.push(s);
  }
  if (validSizes.length === 0) return [];

  const results: GridRegion[][] = [];
  const seen = new Set<string>();

  for (let attempt = 0; attempt < count * 10 && results.length < count; attempt++) {
    // Build bands: randomly pick valid heights that sum to gridHeight
    const bands: number[] = [];
    let remaining = gridHeight;

    while (remaining > 0) {
      const fitting = validSizes.filter((s) => s <= remaining);
      if (fitting.length === 0) break;
      // Ensure the remainder can still be filled
      const usable = fitting.filter((s) => {
        const rem = remaining - s;
        return rem === 0 || validSizes.some((f) => f <= rem);
      });
      if (usable.length === 0) break;
      const size = usable[Math.floor(Math.random() * usable.length)];
      bands.push(size);
      remaining -= size;
    }

    if (remaining !== 0) continue; // didn't tile perfectly

    // Deduplicate
    const key = bands.join(',');
    if (seen.has(key)) continue;
    seen.add(key);

    // Convert bands to square regions
    const regions: GridRegion[] = [];
    let row = 0;
    for (const h of bands) {
      for (let col = 0; col < gridWidth; col += h) {
        regions.push({ row, col, width: h, height: h });
      }
      row += h;
    }
    results.push(regions);
  }

  return results;
}

/**
 * Analyze a set of regions against a grid. Returns per-region analysis,
 * a numeric score, and whether all constraints are satisfied.
 */
function analyzePartition(
  grid: boolean[][],
  regions: GridRegion[],
  noEmpty: boolean,
  requireUnique: boolean,
  minDiffRank: number,
  maxDiffRank: number,
  hasDiffConstraint: boolean,
  minSweepCount: number,
  maxSweepCount: number,
  hasSweepConstraint: boolean,
): { analysis: RegionAnalysis[]; score: number; satisfied: boolean } {
  const analysis: RegionAnalysis[] = [];
  let score = 0;
  let satisfied = true;

  for (const region of regions) {
    if (isRegionEmpty(grid, region)) {
      analysis.push({ empty: true, unique: null, difficulty: null, sweepCount: null, requiresBacktracking: null });
      if (noEmpty) satisfied = false;
      score -= 10;
      continue;
    }

    const slice = sliceGrid(grid, region);
    const puzzle = puzzleFromShape(slice);
    if (!puzzle) {
      analysis.push({ empty: false, unique: null, difficulty: null, sweepCount: null, requiresBacktracking: null });
      score -= 5;
      satisfied = false;
      continue;
    }

    const unique = isUnique(puzzle);
    if (!unique) {
      if (requireUnique) satisfied = false;
      score -= 5;
    } else {
      score += 5;
    }

    let difficulty: DifficultyRating | null = null;
    let sweepCount: number | null = null;
    let requiresBacktracking: boolean | null = null;

    // Always run solver when there's a sweep constraint, difficulty constraint, or unique puzzle
    if (hasSweepConstraint || hasDiffConstraint || unique) {
      const result = solve(puzzle);
      if (result.solved) {
        sweepCount = result.sweepCount;
        requiresBacktracking = result.requiresBacktracking;

        difficulty = rateDifficulty(result);
        const rank = DIFFICULTY_RANK[difficulty];
        if (hasDiffConstraint && (rank < minDiffRank || rank > maxDiffRank)) {
          satisfied = false;
          score -= 3;
        } else if (hasDiffConstraint) {
          score += 2;
        }

        if (hasSweepConstraint && (sweepCount < minSweepCount || sweepCount > maxSweepCount)) {
          satisfied = false;
          score -= 3;
        } else if (hasSweepConstraint) {
          score += 2;
        }

        if (!hasDiffConstraint && !hasSweepConstraint) {
          score += 2;
        }
      }
    }

    analysis.push({ empty: false, unique, difficulty, sweepCount, requiresBacktracking });
  }

  return { analysis, score, satisfied };
}

/**
 * Auto-slice a solution grid into rectangular regions using BSP partitioning.
 *
 * Generates up to `maxAttempts` random partitions and returns the best one.
 * Uses a two-phase approach for performance:
 * - Phase 1 (cheap): reject partitions with empty regions when `noEmpty` is set
 * - Phase 2 (expensive): evaluate difficulty and uniqueness only for viable candidates
 *
 * Stops early when a partition satisfying all constraints is found.
 */
export function autoSlice(
  grid: boolean[][],
  gridWidth: number,
  gridHeight: number,
  options: AutoSliceOptions = {},
): AutoSliceResult {
  const minSize = options.minSize ?? 3;
  const maxSize = Math.min(options.maxSize ?? 8, Math.max(gridWidth, gridHeight));
  const noEmpty = options.noEmpty ?? false;
  const requireUnique = options.requireUnique ?? false;
  const maxAttempts = options.maxAttempts ?? 10_000;

  const hasDiffConstraint = options.minDifficulty != null || options.maxDifficulty != null;
  const minDiffRank = options.minDifficulty != null ? DIFFICULTY_RANK[options.minDifficulty] : 0;
  const maxDiffRank = options.maxDifficulty != null ? DIFFICULTY_RANK[options.maxDifficulty] : 4;

  const hasSweepConstraint = options.minSweepCount != null || options.maxSweepCount != null;
  const minSweepCnt = options.minSweepCount ?? 0;
  const maxSweepCnt = options.maxSweepCount ?? Infinity;

  let bestRegions: GridRegion[] = [];
  let bestAnalysis: RegionAnalysis[] = [];
  let bestScore = -Infinity;
  let bestSatisfied = false;
  let evaluated = 0;

  // --- Uniform mode: square regions in horizontal bands ---
  if (options.uniformSize) {
    const candidates = squarePartitions(gridWidth, gridHeight, minSize, maxSize, maxAttempts);
    for (const regions of candidates) {
      evaluated++;
      const { analysis, score, satisfied } = analyzePartition(
        grid, regions, noEmpty, requireUnique, minDiffRank, maxDiffRank, hasDiffConstraint,
        minSweepCnt, maxSweepCnt, hasSweepConstraint,
      );
      if (score > bestScore) {
        bestScore = score;
        bestRegions = regions;
        bestAnalysis = analysis;
        bestSatisfied = satisfied;
      }
      if (satisfied) break;
    }
    return { regions: bestRegions, analysis: bestAnalysis, constraintsSatisfied: bestSatisfied, attempts: evaluated };
  }

  // --- BSP mode ---

  // Fallback: best partition that failed the cheap empty-region filter
  // (fewest empty regions). Used when no partition passes the filter.
  let fallbackRegions: GridRegion[] = [];
  let fallbackEmptyCount = Infinity;

  for (let i = 0; i < maxAttempts; i++) {
    const regions = bspPartition(0, 0, gridWidth, gridHeight, minSize, maxSize);

    // Phase 1: cheap empty-region filter
    if (noEmpty) {
      let emptyCount = 0;
      for (const region of regions) {
        if (isRegionEmpty(grid, region)) emptyCount++;
      }
      if (emptyCount > 0) {
        // Track as fallback (prefer fewest empty regions)
        if (emptyCount < fallbackEmptyCount) {
          fallbackEmptyCount = emptyCount;
          fallbackRegions = regions;
        }
        continue; // skip expensive analysis
      }
    }

    // Phase 2: full analysis (expensive)
    evaluated++;
    const { analysis, score, satisfied } = analyzePartition(
      grid,
      regions,
      noEmpty,
      requireUnique,
      minDiffRank,
      maxDiffRank,
      hasDiffConstraint,
      minSweepCnt,
      maxSweepCnt,
      hasSweepConstraint,
    );

    if (score > bestScore) {
      bestScore = score;
      bestRegions = regions;
      bestAnalysis = analysis;
      bestSatisfied = satisfied;
    }

    if (satisfied) break; // all constraints met — done
  }

  // If no partition passed the empty filter, fall back to the least-empty one
  if (bestRegions.length === 0 && fallbackRegions.length > 0) {
    evaluated++;
    const { analysis, satisfied } = analyzePartition(
      grid,
      fallbackRegions,
      noEmpty,
      requireUnique,
      minDiffRank,
      maxDiffRank,
      hasDiffConstraint,
      minSweepCnt,
      maxSweepCnt,
      hasSweepConstraint,
    );
    bestRegions = fallbackRegions;
    bestAnalysis = analysis;
    bestSatisfied = satisfied;
  }

  return {
    regions: bestRegions,
    analysis: bestAnalysis,
    constraintsSatisfied: bestSatisfied,
    attempts: evaluated,
  };
}
